export const APP_EMAIL_DOMAIN = 'rsa.com.pk';
// export const APP_EMAIL_DOMAIN = 'portuspk.com';


/*
 * Security note:
 * Keep every official domain here. Firestore rules and server-side blocking
 * functions must use the same allow-list. Do not add disposable/public domains.
 */
export const APP_EMAIL_DOMAINS = [
  'rsa.com.pk',
];
// export const APP_EMAIL_DOMAINS = [
//   'portuspk.com',
// ];

export const BLOCKED_EMAIL_DOMAINS = [
  'mailinator.com',
  'yopmail.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'sharklasers.com',
];

export const COMPANY_REF_PREFIX = 'RSA';

export const APP_EMAIL_SUFFIX = `@${APP_EMAIL_DOMAIN}`;

export function getEmailDomain(email: string): string {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const parts = normalizedEmail.split('@');

  return parts.length === 2 ? parts[1] : '';
}

export function isAllowedCompanyEmail(email: string): boolean {
  const domain = getEmailDomain(email);

  return (
    !!domain &&
    APP_EMAIL_DOMAINS.includes(domain) &&
    !BLOCKED_EMAIL_DOMAINS.includes(domain)
  );
}

export function allowedEmailDomainsLabel(): string {
  return APP_EMAIL_DOMAINS
    .map(domain => `@${domain}`)
    .join(', ');
}

export interface CompanyDetails {
  name: string;
  address: string;
  phone: string;
  email: string;
  domain?: string;
  ntn: string;
}

const COMPANY_STORAGE_KEYS = [
  'companyDetails',
  'selectedCompany',
  'companyData',
  'currentCompany',
  'companies',
  'company',
];

function safeJsonParse(raw: string | null): any {
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function pickFirstValue(...values: any[]): string {
  for (const value of values) {
    if (value === undefined || value === null) continue;

    const text = String(value).trim();

    if (text) return text;
  }

  return '';
}

function normalizeCompanyObject(value: any): any {
  if (!value) return null;

  if (Array.isArray(value)) {
    return value.find(item => item && typeof item === 'object') || null;
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
}

export function normalizeEmailDomain(email: string): string {
  return String(email || '')
    .trim()
    .replace(
      /@rsa\.com\.pk$/i,
      APP_EMAIL_SUFFIX
    );
}

export function getCompanyDetails(): CompanyDetails {
  let company: any = null;

  for (const key of COMPANY_STORAGE_KEYS) {
    const parsed = normalizeCompanyObject(
      safeJsonParse(localStorage.getItem(key))
    );

    if (parsed) {
      company = parsed;
      break;
    }
  }

  company = company || {};

  return {
    name: pickFirstValue(
      company.companyName,
      company.name,
      company.title,
      company.businessName
    ),
    address: pickFirstValue(
      company.address,
      company.companyAddress,
      company.officeAddress,
      company.businessAddress,
      company.location
    ),
    phone: pickFirstValue(
      company.phone,
      company.telePhone,
      company.phoneNo,
      company.phoneNumber,
      company.mobile,
      company.contact,
      company.contactNo,
      company.contactNumber,
      company.number
    ),
    email: normalizeEmailDomain(
      pickFirstValue(
        company.email,
        company.companyEmail,
        company.officialEmail,
        company.businessEmail,
        company.contactEmail
      )
    ),
    domain: pickFirstValue(
      company.domain,
      company.companyDomain,
      company.websiteDomain,
      company.webDomain
    ),
    ntn: pickFirstValue(
      company.ntn,
      company.NTN,
      company.ntnNo,
      company.ntnNumber,
      company.taxNumber,
      company.taxNo,
      company.companyNtn
    ),
  };
}

export function getCompanyContactLine(
  company: CompanyDetails = getCompanyDetails()
): string {
  return [
    company.phone ? `Tel: ${company.phone}` : '',
    company.email ? `Email: ${company.email}` : '',
    company.domain ? `Domain: ${company.domain}` : '',
    company.ntn ? `NTN: ${company.ntn}` : '',
  ]
    .filter(Boolean)
    .join(' | ');
}

export function getCompanyBankTitle(
  company: CompanyDetails = getCompanyDetails()
): string {
  return company.name || '';
}
